// react
import { useEffect, useState } from 'react';

import proj4 from 'proj4';

const WGS84 = '+proj=longlat +datum=WGS84 +no_defs +type=crs';
const ETRS89_UTM32 =
  '+proj=utm +zone=32 +ellps=GRS80 +units=m +no_defs +type=crs';

const DAR_REVERSE_GEOCODE_QUERY = `
  query DarReverseGeocodeByPoint($wkt: String!, $registreringstid: DafDateTime!) {
    DAR_Adressepunkt(
      first: 1
      registreringstid: $registreringstid
      where: { position: { intersects: { crs: 25832, wkt: $wkt } } }
    ) {
      nodes {
        id_lokalId
      }
    }
  }
`;

const DAR_HUSNUMMER_BY_ADRESSEPUNKT_QUERY = `
  query DarHusnummerByAdressepunkt($adgangspunktId: String!, $registreringstid: DafDateTime!) {
    DAR_Husnummer(
      first: 1
      registreringstid: $registreringstid
      where: { adgangspunkt: { eq: $adgangspunktId } }
    ) {
      nodes {
        adgangsadressebetegnelse
      }
    }
  }
`;

const toUtm32WktPoint = (latitude, longitude) => {
  const lat = Number(latitude);
  const lon = Number(longitude);

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

  try {
    const [x, y] = proj4(WGS84, ETRS89_UTM32, [lon, lat]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

    return `POINT(${x} ${y})`;
  } catch {
    return null;
  }
};

const fetchDarAddressLabel = async ({ wkt, registreringstid, signal }) => {
  const addressPointResponse = await fetch('/dar-graphql', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query: DAR_REVERSE_GEOCODE_QUERY,
      variables: {
        wkt,
        registreringstid,
      },
    }),
    signal,
  });

  if (!addressPointResponse.ok) return null;

  const addressPointPayload = await addressPointResponse.json();
  const adgangspunktId =
    addressPointPayload?.data?.DAR_Adressepunkt?.nodes?.[0]?.id_lokalId;

  if (!adgangspunktId) return null;

  const husnummerResponse = await fetch('/dar-graphql', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query: DAR_HUSNUMMER_BY_ADRESSEPUNKT_QUERY,
      variables: {
        adgangspunktId,
        registreringstid,
      },
    }),
    signal,
  });

  if (!husnummerResponse.ok) return null;

  const husnummerPayload = await husnummerResponse.json();
  const addressLabel =
    husnummerPayload?.data?.DAR_Husnummer?.nodes?.[0]?.adgangsadressebetegnelse;

  return typeof addressLabel === 'string' && addressLabel.trim().length > 0
    ? addressLabel
    : null;
};

/**
 * Reverse geocodes lat/lon to Danish address text.
 * Uses DAR GraphQL only.
 *
 * @param {number|null} latitude
 * @param {number|null} longitude
 * @returns {string|null}
 */
const useDarReverseGeocode = (latitude, longitude) => {
  const [addressLabel, setAddressLabel] = useState(null);

  useEffect(() => {
    if (latitude == null || longitude == null) {
      return;
    }

    const controller = new AbortController();
    const wkt = toUtm32WktPoint(latitude, longitude);
    const registreringstid = new Date().toISOString();

    const resolveAddress = async () => {
      try {
        if (wkt) {
          const darAddress = await fetchDarAddressLabel({
            wkt,
            registreringstid,
            signal: controller.signal,
          });

          if (darAddress) {
            setAddressLabel(darAddress);
            return;
          }
        }

        setAddressLabel(null);
      } catch {
        // Ignore network and abort errors.
      }
    };

    void resolveAddress();

    return () => controller.abort();
  }, [latitude, longitude]);

  return latitude == null || longitude == null ? null : addressLabel;
};

export default useDarReverseGeocode;
